import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../context/ToastContext';
import { Ingredient } from '../types/Types';
import { useSubprocesses } from '../select/SubprocessCells';
import { usePhaseTypeOptions } from '../select/PhaseTypeSelect';
import { useMachineOptions } from '../select/MachineSelect';
import { useDocumentTypesContext } from '../context/DocumentTypesContext';
import { getCachedUnitMeasurmentCode } from '../select/UnitMeasurmentSelect';
import { resolveSubprocessNamesText } from '../components/OSSubprocessCheckboxes';
import { downloadIngredientsExcel, IngredientExportFilters } from '../utils/osExcel';
import { downloadOSHeaderExcel } from '../utils/osHeaderExcel';
import { downloadOSRazmerExcel } from '../utils/osRazmerExcel';
import { downloadOSInterProductsExcel } from '../utils/osInterProductsExcel';
import { downloadOSProductivityExcel } from '../utils/osProductivityExcel';
import { orderedMachineIds } from '../utils/logbookStage';
import { useRazmerProcessDefinition } from '../components/OSStageTree';
import { downloadOSMaterialnoExcel } from '../utils/osMaterialnoExcel';
import { downloadOSSubprocessEntriesExcel } from '../utils/osSubprocessEntriesExcel';
import { RazmerRow } from '../components/OSRazmerTable';
import { MatGroup } from '../components/OSMaterialnoTable';
import { ProdRow } from '../components/OSProductivityTable';
import { SubprocessItemRow } from '../components/OSSubprocessItemsTable';
import { allowedFinishedYieldOf, allowedFinishedWasteOf } from '../components/OSDetailHeader';
import { User } from '../types/Types';

/**
 * Every Excel export offered by the OS screen's ExcelExportDropdown.
 *
 * Extracted out of screens/OS.tsx, which carried nine near-identical handlers plus the
 * codebook lookups they need. The codebooks (subprocesses / phase types / machines /
 * document types) are resolved HERE rather than passed in — they are module-cached, so
 * calling their hooks again costs no extra request, and it keeps four imports and three
 * hook calls out of the screen that only ever existed to feed these exports.
 *
 * All per-section exports are client-side: the rows are already in form state, and they
 * cover the WHOLE normativ (every subprocess), never just the active stage's slice.
 */

export interface OSExportsParams {
  /** Everything the exports read out of the open normativ's form state. */
  ingredient: Ingredient | null;
  selectedItemCode: string;
  selectedWorkOrderDocumentId: string;
  validFrom: string;
  validTo: string;
  productionCode: string;
  versionDisplay: string;
  batch: number;
  batchUnit: string | null;
  batchUnitCode: string;
  waste: number;
  plannedWaste: number;
  status: string[];
  preparedUserId: string;
  preparedDate: string;
  approvedUserId: string;
  approvedDate: string;
  usersList: User[];
  selectedSubprocessIds: string[];
  subprocessItemRows: SubprocessItemRow[];
  productivityRows: ProdRow[];
  razmerRows: RazmerRow[];
  materialnoGroups: MatGroup[];
  inProcessValues: Record<string, string>;
  montageValues: Record<string, string>;
  /** Called after a successful filtered export so the screen can close its filter modal. */
  onFilteredExported?: () => void;
}

export const useOSExports = (p: OSExportsParams) => {
  const { t, i18n } = useTranslation();
  const { showToast } = useToast();
  const subprocesses = useSubprocesses();
  const phaseTypeOptions = usePhaseTypeOptions();
  const machineOptions = useMachineOptions();
  const razmerProcessDefinition = useRazmerProcessDefinition();
  const { documentTypes } = useDocumentTypesContext();

  const [isExporting, setIsExporting] = useState(false);

  const mainName =
    (p.ingredient?.items && typeof p.ingredient.items === 'object' ? p.ingredient.items.name : '') || '';

  /**
   * Same name-resolution as OSSignaturePanel's user select, so an export shows exactly what
   * the signature dropdowns display rather than a raw ObjectId.
   */
  const resolveUserName = useCallback((userId: string): string => {
    const u = p.usersList.find((x) => x._id === userId);
    if (!u) return '';
    return `${u.firstName || ''} ${u.lastName || ''}`.trim() || (u as any).userName || (u as any).email || u._id;
  }, [p.usersList]);

  /** Whole-codebook export (all normativs) — server-rendered, hence async + toasts. */
  const exportAll = async () => {
    setIsExporting(true);
    try {
      await downloadIngredientsExcel(i18n.language);
      showToast(t('messages.excelDownloadSuccess') || 'Excel downloaded', 'success');
    } catch {
      showToast(t('errors.excelDownloadFailed') || 'Export failed', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  const exportFiltered = async (filters: IngredientExportFilters) => {
    setIsExporting(true);
    try {
      await downloadIngredientsExcel(i18n.language, filters);
      showToast(t('messages.excelDownloadSuccess') || 'Excel downloaded', 'success');
      p.onFilteredExported?.();
    } catch {
      showToast(t('errors.excelDownloadFailed') || 'Export failed', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  /** Заглавие of the open normativ. */
  const exportHeader = () => {
    const workOrderDoc = p.selectedWorkOrderDocumentId
      ? documentTypes.find((d) => d._id === p.selectedWorkOrderDocumentId)
      : undefined;
    downloadOSHeaderExcel({
      ident: p.selectedItemCode,
      name: mainName,
      workOrder: workOrderDoc?.code || '',
      validFrom: p.validFrom,
      validTo: p.validTo,
      productionCode: p.productionCode,
      versionDisplay: p.versionDisplay,
      batch: p.batch ?? null,
      batchUnitCode: p.batchUnitCode || getCachedUnitMeasurmentCode(p.batchUnit),
      allowedYieldValue: allowedFinishedYieldOf(p.batch ?? null, p.ingredient?.items),
      allowedWasteValue: allowedFinishedWasteOf(p.ingredient?.items),
      statusText: p.status.map((s) => t(`status.${s}`)).join(', '),
      preparedName: resolveUserName(p.preparedUserId),
      preparedDate: p.preparedDate || '',
      approvedName: resolveUserName(p.approvedUserId),
      approvedDate: p.approvedDate || '',
      subprocessesText: resolveSubprocessNamesText(p.selectedSubprocessIds, subprocesses),
    });
  };

  const exportInterProducts = () =>
    downloadOSInterProductsExcel({
      rows: p.subprocessItemRows,
      subprocesses,
      mainIdent: p.selectedItemCode,
      mainName,
    });

  const exportProductivity = () =>
    downloadOSProductivityExcel({
      rows: p.productivityRows,
      subprocesses,
      machines: machineOptions,
      razmerMachineIds: orderedMachineIds(razmerProcessDefinition?.machines),
      mainIdent: p.selectedItemCode,
      mainName,
    });

  const exportRazmer = () =>
    downloadOSRazmerExcel({
      rows: p.razmerRows,
      subprocesses,
      phaseTypeOptions,
      mainIdent: p.selectedItemCode,
      mainName,
    });

  const exportMaterialno = () =>
    downloadOSMaterialnoExcel({
      groups: p.materialnoGroups,
      subprocesses,
      mainIdent: p.selectedItemCode,
      mainName,
    });

  const exportInProcess = () =>
    downloadOSSubprocessEntriesExcel({
      subprocesses,
      values: p.inProcessValues,
      entriesKey: 'inProcess',
      entriesLabel: 'InProcess',
      sheetName: 'InProcess',
      filenamePrefix: 'inprocess',
      mainIdent: p.selectedItemCode,
      mainName,
    });

  const exportMontageParams = () =>
    downloadOSSubprocessEntriesExcel({
      subprocesses,
      values: p.montageValues,
      entriesKey: 'montageParams',
      entriesLabel: 'Параметри за монтажа',
      sheetName: 'Параметри за монтажа',
      filenamePrefix: 'montaza',
      mainIdent: p.selectedItemCode,
      mainName,
    });

  /**
   * Menu-item gates: only offer the two entry exports when the CURRENT normativ's stages
   * actually define entries of that kind. `subprocesses` is the shared codebook, not a
   * per-normativ list, so it has to be intersected with this normativ's stages.
   */
  const hasInProcessEntries = subprocesses.some(
    (sp) => p.selectedSubprocessIds.includes(sp._id) && (sp.inProcess?.length ?? 0) > 0
  );
  const hasMontageParamsEntries = subprocesses.some(
    (sp) => p.selectedSubprocessIds.includes(sp._id) && (sp.montageParams?.length ?? 0) > 0
  );

  return {
    isExporting,
    exportAll,
    exportFiltered,
    exportHeader,
    exportInterProducts,
    exportProductivity,
    exportRazmer,
    exportMaterialno,
    exportInProcess,
    exportMontageParams,
    hasInProcessEntries,
    hasMontageParamsEntries,
    /** Also used by the archive info box to render "Архивирал". */
    resolveUserName,
  };
};
